import * as React from 'react';
import { useCallback } from 'react';

import type { InteractProductCard } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useUserFeedback } from 'bundles/page-config-common/contexts/UserFeedbackContext';
import type { FeedbackOption } from 'bundles/page-config-common/types/userFeedback';
import type { FeedbackButtonRef } from 'bundles/product-card/components/recs-feedback-experiment/FeedbackButtons';
import FeedbackButtons from 'bundles/product-card/components/recs-feedback-experiment/FeedbackButtons';

type Props = {
  productCardVariant?: 'grid' | 'list';
  productType?: string;
  productName?: string;
  initialFeedbackState?: FeedbackOption;
  feedbackMetadata?: {
    shortProductId: string;
    fullProductId: string;
    entityType: string;
    collectionId: string;
    collectionName: string;
  };
  trackingData?: Omit<InteractProductCard, 'interactType' | 'page' | 'user_properties'>;
  onFeedbackDislike?: () => void;
  dislikeButtonRef: React.Ref<FeedbackButtonRef>;
  setFeedbackMap?: React.Dispatch<React.SetStateAction<Record<string, FeedbackOption>>>;
};

const EMPTY_FEEDBACK_METADATA = {
  shortProductId: '',
  fullProductId: '',
  entityType: '',
  collectionId: '',
  collectionName: '',
};

const EMPTY_TRACKING_DATA = {
  carousel: { id: '', name: '' },
  product: { id: '', name: '' },
  productCard: { index: 0 },
};

const FeedbackContainer = ({
  productCardVariant = 'grid',
  productType,
  productName,
  initialFeedbackState = '',
  feedbackMetadata = EMPTY_FEEDBACK_METADATA,
  trackingData = EMPTY_TRACKING_DATA,
  onFeedbackDislike,
  dislikeButtonRef,
  setFeedbackMap,
}: Props) => {
  const { sendFeedback } = useUserFeedback();
  const [currentFeedbackState, setCurrentFeedbackState] = React.useState(initialFeedbackState);
  const track = useTracker();

  const handleThumbsUpClick = useCallback(
    (prevFeedbackState) => {
      if (prevFeedbackState === 'LIKE') {
        sendFeedback?.({ ...feedbackMetadata, feedback: '' });
        track('interact_product_card', { ...trackingData, interactType: 'undo_like_recommendation' });
        setCurrentFeedbackState('');
        setFeedbackMap?.((feedbackMap) => ({ ...feedbackMap, [feedbackMetadata.shortProductId]: '' }));
      } else {
        sendFeedback?.({ ...feedbackMetadata, feedback: 'LIKE' });
        track('interact_product_card', { ...trackingData, interactType: 'like_recommendation' });
        setCurrentFeedbackState('LIKE');
        setFeedbackMap?.((feedbackMap) => ({ ...feedbackMap, [feedbackMetadata.shortProductId]: 'LIKE' }));
      }
    },
    [sendFeedback, feedbackMetadata, track, trackingData, setFeedbackMap]
  );

  const handleThumbsDownClick = useCallback(
    (prevFeedbackState) => {
      if (prevFeedbackState === 'DISLIKE') {
        sendFeedback?.({ ...feedbackMetadata, feedback: '' });
        track('interact_product_card', { ...trackingData, interactType: 'undo_dislike_recommendation' });
        setCurrentFeedbackState('');
        setFeedbackMap?.((feedbackMap) => ({ ...feedbackMap, [feedbackMetadata.shortProductId]: '' }));
      } else {
        sendFeedback?.({ ...feedbackMetadata, feedback: 'DISLIKE' });
        track('interact_product_card', { ...trackingData, interactType: 'dislike_recommendation' });
        setCurrentFeedbackState('DISLIKE');
        setFeedbackMap?.((feedbackMap) => ({ ...feedbackMap, [feedbackMetadata.shortProductId]: 'DISLIKE' }));
        onFeedbackDislike?.();
      }
    },
    [sendFeedback, feedbackMetadata, onFeedbackDislike, track, trackingData, setFeedbackMap]
  );

  return (
    <FeedbackButtons
      productCardVariant={productCardVariant}
      productType={productType}
      productName={productName}
      currentFeedbackState={currentFeedbackState}
      onThumbsUpClick={handleThumbsUpClick}
      onThumbsDownClick={handleThumbsDownClick}
      ref={dislikeButtonRef}
    />
  );
};

export default FeedbackContainer;
